/**
 * The pull state's chip — one component for the gang-sheet card, the run page, the rail and the press
 * station's batch filter (pulled-blanks v2 §2.5 door 3, §5). The words come from `pullChipLabel`; this
 * only picks the tone: amber for "started · not confirmed" (the one state the count asks about) and for
 * a closed sheet's leftover, violet for a record, muted for the rest.
 */
import { cn } from '@/lib/utils';
import { type BatchStatus, type PullState, pullChipLabel } from './batch-lib';

type Tone = 'muted' | 'amber' | 'violet';

const TONE_CLASS: Record<Tone, string> = {
  muted: 'border-border bg-muted text-muted-foreground',
  amber:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
  violet:
    'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300',
};

export function pullChipTone(state: PullState, status: BatchStatus): Tone {
  switch (state.kind) {
    case 'started':
      return 'amber';
    case 'pulled':
      return status === 'PRINTED' && state.stillOut.length > 0 ? 'amber' : 'violet';
    default:
      return 'muted';
  }
}

export function PullChip({
  state,
  status,
  now,
  form = 'long',
  /** Rail rows stay quiet on an untouched sheet — the chip is for a sheet someone has started or pulled. */
  hideNotStarted = false,
  className,
}: {
  state: PullState;
  status: BatchStatus;
  now: number;
  form?: 'long' | 'short';
  hideNotStarted?: boolean;
  className?: string;
}) {
  if (hideNotStarted && (state.kind === 'not-started' || state.kind === 'not-ready')) return null;
  const label = pullChipLabel(state, status, now, form);
  if (!label) return null;
  return (
    <span
      data-testid="pull-chip"
      data-pull-state={state.kind}
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs',
        TONE_CLASS[pullChipTone(state, status)],
        className,
      )}
    >
      {label}
    </span>
  );
}
